import React, { useEffect, useMemo, useState } from "react";
import { Box, TextField, InputAdornment, TablePagination, TableSortLabel, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import moment from "moment-timezone";

import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import { tokens } from "../../theme";
import { mono, pill, th, td, TablePaginationActions } from "../../components/tableKit";

const fmtDateTime = (d) =>
  d && moment.utc(d).isValid() ? moment.utc(d).format("YYYY-MM-DD HH:mm:ss") : "";

const typeKind = (t) => {
  const s = String(t || "").toLowerCase();
  if (s.includes("withdraw")) return "bad";
  if (s.includes("deposit")) return "warn";
  if (s.includes("kyc")) return "neutral";
  return "neutral";
};

export default function ErrorLog() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdOn", direction: "desc" });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const response = await getAllData(endPoints.api.GET_ERROR_LOG);
      if (response?.status === "success") {
        setRows(response.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
    setPage(0);
  };

  const rowsToDisplay = useMemo(() => {
    let result = rows;

    if (searchText) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (r) =>
          String(r.userId || "").includes(q) ||
          String(r.errorCode || "").toLowerCase().includes(q) ||
          String(r.errorType || "").toLowerCase().includes(q) ||
          String(r.errorMessage || "").toLowerCase().includes(q)
      );
    }

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let av = a[sortConfig.key] ?? "";
        let bv = b[sortConfig.key] ?? "";
        let cmp;
        if (!isNaN(Number(av)) && !isNaN(Number(bv)) && av !== "" && bv !== "") {
          cmp = Number(av) - Number(bv);
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
        }
        return sortConfig.direction === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, searchText, sortConfig]);

  const sortableTh = (key, label) => (
    <th style={th}>
      <TableSortLabel
        active={sortConfig.key === key}
        direction={sortConfig.key === key ? sortConfig.direction : "desc"}
        onClick={() => handleSort(key)}
        sx={{ "&.MuiTableSortLabel-root": { color: "inherit" }, "& .MuiTableSortLabel-icon": { fontSize: 14 } }}
      >
        {label}
      </TableSortLabel>
    </th>
  );

  const last24h = rows.filter(
    (r) => r.createdOn && moment.utc(r.createdOn).isAfter(moment.utc().subtract(24, "hours"))
  ).length;
  const affectedUsers = new Set(rows.map((r) => r.userId).filter(Boolean)).size;

  return (
    <Box>
      <Toaster></Toaster>
      {isLoading && <Loader spinner={true} visible={isLoading} />}

      <PageHeader
        title="Error Log"
        crumb="Operate / Error Log"
        subtitle="Payment, withdrawal & KYC errors logged across the platform"
      />

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.75, mb: 1.75 }}>
        <Box sx={{ flex: "1 1 200px" }}>
          <MiniStat label="Total errors" value={String(rows.length)} color={tokens.danger} />
        </Box>
        <Box sx={{ flex: "1 1 200px" }}>
          <MiniStat label="Last 24 hours" value={String(last24h)} note="notification feed" color={tokens.amber} />
        </Box>
        <Box sx={{ flex: "1 1 200px" }}>
          <MiniStat label="Affected users" value={String(affectedUsers)} color={tokens.primary} />
        </Box>
      </Box>

      <SectionCard>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by user ID, code, type or message"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 320, "& .MuiInputBase-root": { fontSize: 13 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {sortableTh("errorType", "Type")}
                {sortableTh("errorCode", "Code")}
                {sortableTh("userId", "User")}
                <th style={th}>Message</th>
                {sortableTh("count", "Count")}
                {sortableTh("createdOn", "Logged At")}
              </tr>
            </thead>
            <tbody>
              {rowsToDisplay.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={5} style={{ ...td, textAlign: "center", color: tokens.textDim }}>
                    No errors logged
                  </td>
                </tr>
              )}
              {rowsToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <tr key={row.id}>
                    <td style={td}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <ReportProblemRoundedIcon sx={{ fontSize: 15, color: tokens.danger }} />
                        {row.errorType ? pill(row.errorType, typeKind(row.errorType)) : "—"}
                      </Box>
                    </td>
                    <td style={{ ...td, fontFamily: mono }}>{row.errorCode}</td>
                    <td style={{ ...td, fontFamily: mono }}>{row.userId ? `#${row.userId}` : "—"}</td>
                    <td style={{ ...td, whiteSpace: "normal", maxWidth: 520, color: tokens.textDim }}>
                      {row.errorMessage}
                    </td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{row.count}</td>
                    <td style={{ ...td, fontFamily: mono, color: tokens.textDim }}>{fmtDateTime(row.createdOn)}</td>
                  </tr>
                ))}
            </tbody>
          </Box>
        </Box>

        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={rowsToDisplay.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          ActionsComponent={TablePaginationActions}
          sx={{ borderTop: `1px solid ${tokens.border}`, mt: 1 }}
        />
      </SectionCard>
    </Box>
  );
}
